import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService, StateUtils } from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, share, switchMap, take } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth';
import {
  CartType,
  Cutting,
  CuttingFormat,
  CuttingOptions,
  CuttingPackaging,
  CuttingPackagingMaxSize,
  CuttingRequest,
  CuttingTemplate,
  CuttingTemplateRequest,
  GrainDirection,
  GrainDirectionType,
  OrderEntry,
} from '../../../../core/model';
import { PrincipalConfigurationService } from '../../../../core/user';
import { withdrawOn } from '../../../../core/util/rxjs';
import { makeCuttingKey, makeCuttingTemplateKey } from '../../../../shared';
import { ActiveCartFacade, CuttingFacade } from '../facades';
import { CartActions } from '../store/actions';
import { StateWithCart } from '../store/cart-state';
import { CartSelectors } from '../store/selectors';

@Injectable()
export class CuttingService implements CuttingFacade {
  constructor(
    private store: Store<StateWithCart>,
    private authService: AuthService,
    private userIdService: UserIdService,
    private activeCartService: ActiveCartFacade,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  getOptions(cartType: CartType): Observable<CuttingOptions> {
    this.assertStockCart(cartType);

    return this.principalConfigurationService.getValues().pipe(
      map((configValues) => {
        const options: CuttingOptions = {
          enabled: configValues['enableCutting']?.value as boolean,
        };
        if (options.enabled) {
          options.dustFree = {
            enabled: configValues['enableCuttingDustFree']?.value as boolean,
            defaultValue: configValues['cuttingDustFreeDefaultValue']?.value as boolean,
          };
          options.perpendicular = {
            enabled: configValues['enableCuttingPerpendicular']?.value as boolean,
            defaultValue: configValues['cuttingPerpendicularDefaultValue']?.value as boolean,
          };
          options.grainDirection = {
            enabled: configValues['enableDisplayGrainDirection']?.value as boolean,
            options: this.populateGrainDirections(),
          };
          options.packaging = {
            options: this.parsePackagings(configValues['cuttingPackagings']?.value as string),
            defaultOption: configValues['cuttingDefaultPackaging']?.value as string,
            largeOptions: this.parsePackagingOptions(configValues['cuttingLargePackagingOptions']?.value as string),
            smallOptions: this.parsePackagingOptions(configValues['cuttingSmallPackagingOptions']?.value as string),
            smallMaxSize: this.parsePackagingMaxSize(configValues['cuttingSmallPackagingMaxSize']?.value as string),
          };
          options.format = {
            options: this.parseFormats(configValues['cuttingPredefinedFormats']?.value as string),
          };
          options.cutting = {
            minimumSize: 100,
            minimumMargin: configValues['cuttingMinimumMargin']?.value as number,
          };
        }
        return options;
      }),
      take(1)
    );
  }

  private populateGrainDirections(): GrainDirection[] {
    const grainDirections: GrainDirection[] = [];
    for (const grainDirectionType in GrainDirectionType) {
      if (typeof GrainDirectionType[grainDirectionType] !== 'string') {
        continue;
      }
      grainDirections.push({
        type: GrainDirectionType[grainDirectionType as keyof typeof GrainDirectionType],
        translationKey: `cutting.grainDirection.${grainDirectionType}`,
      });
    }
    return grainDirections;
  }

  private parsePackagings(value: string): CuttingPackaging[] {
    const packagings: CuttingPackaging[] = [];
    value
      .trim()
      .split(',')
      .map((packaging) => {
        const parts = packaging.trim().split(':');
        packagings.push({
          code: parts[0].trim().toLowerCase(),
          translationKey: parts[1].trim(),
          defaultTranslation: parts[2].trim(),
        });
      });
    return packagings;
  }

  private parsePackagingOptions(value: string): string[] {
    return value
      .trim()
      .split(',')
      .map((option) => option.trim().toLowerCase());
  }

  private parsePackagingMaxSize(value: string): CuttingPackagingMaxSize {
    const parts = value.trim().split('x');
    const part0 = parseInt(parts[0], 10);
    const part1 = parseInt(parts[1], 10);
    return {
      shortSide: Math.min(part0, part1),
      longSide: Math.max(part0, part1),
    };
  }

  private parseFormats(value: string): CuttingFormat[] {
    const formats: CuttingFormat[] = [];
    value
      .trim()
      .split(',')
      .map((format) => {
        const parts = format.trim().split(':');
        formats.push({
          code: parts[0],
          width: parseInt(parts[1], 10),
          length: parseInt(parts[2], 10),
        });
      });
    return formats;
  }

  private loadTemplate(
    cartType: CartType,
    cartId: string,
    entry: OrderEntry,
    request: CuttingTemplateRequest,
    key: string
  ): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new CartActions.CalculateCuttingTemplate({
            cartType,
            cartId,
            userId,
            entry,
            cuttingTemplate: request,
            key,
          })
        ),
      () => {}
    );
  }

  calculateTemplate(
    entry: OrderEntry,
    request: CuttingTemplateRequest,
    cartType: CartType
  ): Observable<CuttingTemplate | undefined> {
    this.assertStockCart(cartType);

    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeCuttingTemplateKey({
            articleCode: entry.articleRef,
            cuttingTemplate: request,
          })
        )
      ),
      switchMap((key) => {
        return combineLatest([
          of(key),
          this.store.select(CartSelectors.getCuttingTemplateStateFactory(key)),
          isUserLoggedIn$,
          this.activeCartService.getActiveCartId(cartType),
        ]);
      }),
      filter(([key, loaderState, isUserLoggedIn, cartId]) => {
        if (isUserLoggedIn && this.needsToLoad(loaderState)) {
          this.loadTemplate(cartType, cartId, entry, request, key);
          return false;
        }
        return loaderState.success;
      }),
      map(([_key, loaderState, _isUserLoggedIn, _cart]) => loaderState.value),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      take(1)
    );
  }

  calculateTemplateLoading(entry: OrderEntry, request: CuttingTemplateRequest, cartType: CartType): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeCuttingTemplateKey({
            articleCode: entry.articleRef,
            cuttingTemplate: request,
          })
        )
      ),
      switchMap((key) => this.store.select(CartSelectors.getCuttingTemplateLoadingFactory(key)))
    );
  }

  calculateTemplateLoaded(entry: OrderEntry, request: CuttingTemplateRequest, cartType: CartType): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeCuttingTemplateKey({
            articleCode: entry.articleRef,
            cuttingTemplate: request,
          })
        )
      ),
      switchMap((key) => this.store.select(CartSelectors.getCuttingTemplateLoadedFactory(key)))
    );
  }

  private loadPrice(cartType: CartType, cartId: string, entry: OrderEntry, request: CuttingRequest, key: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new CartActions.CalculateCuttingPrice({
            cartType,
            cartId,
            userId,
            entry,
            cutting: request,
            key,
          })
        ),
      () => {}
    );
  }

  calculatePrice(entry: OrderEntry, request: CuttingRequest, cartType: CartType): Observable<Cutting | undefined> {
    this.assertStockCart(cartType);

    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeCuttingKey({
            articleCode: entry.articleRef,
            quantity: entry.quantity,
            unit: entry.unit,
            cutting: request,
          })
        )
      ),
      switchMap((key) => {
        return combineLatest([
          of(key),
          this.store.select(CartSelectors.getCuttingPriceStateFactory(key)),
          isUserLoggedIn$,
          this.activeCartService.getActiveCartId(cartType),
        ]);
      }),
      filter(([key, loaderState, isUserLoggedIn, cartId]) => {
        if (isUserLoggedIn && this.needsToLoad(loaderState)) {
          this.loadPrice(cartType, cartId, entry, request, key);
          return false;
        }
        return loaderState.success;
      }),
      map(([_key, loaderState, _isUserLoggedIn, _cart]) => loaderState.value),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      take(1)
    );
  }

  calculatePriceLoading(entry: OrderEntry, request: CuttingRequest, cartType: CartType): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeCuttingKey({
            articleCode: entry.articleRef,
            quantity: entry.quantity,
            unit: entry.unit,
            cutting: request,
          })
        )
      ),
      switchMap((key) => this.store.select(CartSelectors.getCuttingPriceLoadingFactory(key)))
    );
  }

  calculatePriceLoaded(entry: OrderEntry, request: CuttingRequest, cartType: CartType): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeCuttingKey({
            articleCode: entry.articleRef,
            quantity: entry.quantity,
            unit: entry.unit,
            cutting: request,
          })
        )
      ),
      switchMap((key) => this.store.select(CartSelectors.getCuttingPriceLoadedFactory(key)))
    );
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<Cutting | CuttingTemplate>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }

  private assertStockCart(cartType: CartType) {
    if (cartType !== CartType.stock) {
      throw new Error('Cutting is only possible for stock carts');
    }
  }
}
